import React, { useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Box,
    Typography,
    Skeleton,
    Button
} from '@mui/material';
import { formatDateTime } from '../utils/dataUtils';
import './DataTable.css';
import * as XLSX from 'xlsx';

const DataTable = ({ data, tableName, loading, hideCapacity }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('DateTime');
    const [order, setOrder] = useState('desc');

    // Helper to get display name for table
    const getDisplayName = (name) => {
        if (!name) return '';
        if (name.toLowerCase() === 'dr-reddy') return 'DR REDDY 4T';
        if (name.toLowerCase() === 'dr-reddy-sez') return 'DR REDDY SEZ';
        if (name.toLowerCase() === 'laxmi-organics') return 'LAXMI ORGANICS';
        if (name.toLowerCase() === 'ks-textile') return 'KS TEXTILE';
        if (name.toLowerCase() === 'clear-choice') return 'CLEAR CHOICE';
        if (name.toLowerCase() === 'baddi') return 'BADDI';
        if (name.toLowerCase() === 'parle') return 'PARLE';
        // ...add more mappings as needed...
        return name.replace(/-/g, ' ').toUpperCase();
    };

    if (!data || data.length === 0) {
        return (
            <Paper elevation={3} className="table-container">
                <Typography variant="h6" component="h2" gutterBottom>
                    {getDisplayName(tableName)}
                </Typography>
                <Typography color="textSecondary">No data available</Typography>
            </Paper>
        );
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortData = (array) => {
        return [...array].sort((a, b) => {
            if (orderBy === 'DateTime') {
                return order === 'asc' 
                    ? new Date(a.DateTime) - new Date(b.DateTime)
                    : new Date(b.DateTime) - new Date(a.DateTime);
            }
            const aValue = a[Object.keys(a).find(key => key.includes(orderBy))] || 0;
            const bValue = b[Object.keys(b).find(key => key.includes(orderBy))] || 0;
            return order === 'asc' ? aValue - bValue : bValue - aValue;
        });
    };

    const LoadingSkeleton = () => (
        <TableRow>
            <TableCell><Skeleton animation="wave" /></TableCell>
            <TableCell><Skeleton animation="wave" /></TableCell>
            <TableCell><Skeleton animation="wave" /></TableCell>
            <TableCell><Skeleton animation="wave" /></TableCell>
            <TableCell><Skeleton animation="wave" /></TableCell>
        </TableRow>
    );

    const getDataKey = (type) => {
        return Object.keys(data[0]).find(key => 
            key.toUpperCase().includes(type.toUpperCase())
        ) || '';
    };

    const getTableHeaders = () => {
        if (tableName.toLowerCase() === 'mohali') {
            return [
                { id: 'DateTime', label: 'Date Time' },
                { id: 'EM_POWER_PRE_1HR', label: 'Power (1 Hour)' },
                { id: 'EM_POWER_PRE_24HR', label: 'Power (24 Hours)' }
            ];
        }
        if (tableName.toLowerCase() === 'laxmi-organics') {
            return [
                { id: 'DateTime', label: 'Date Time' },
                { id: 'FEED_FLOW_TOT_CUR_1H_LAXMI3', label: 'Feed Flow (m³)' },
                { id: 'COND_FLOW_TOT_CUR_1H_LAXMI3', label: 'Cond Flow (m³)' },
                { id: 'EM_POWER_TOT_CUR_1H_LAXMI3', label: 'Power Consumption (kW)' }
            ];
        }
        if (tableName.toLowerCase() === 'ks-textile' || tableName.toLowerCase() === 'clear-choice') {
            // Hide capacity columns if hideCapacity is true
            const baseHeaders = [
                { id: 'DateTime', label: 'Date Time' },
                { id: tableName.toLowerCase() === 'ks-textile' ? 'FEED_FLOW_PRE_1H_TOT_KS' : 'FEED_FLOW_TOT_PRE_1H_CCHOICE', label: 'Feed Flow (m³)' },
                { id: tableName.toLowerCase() === 'ks-textile' ? 'COND_FLOW_PRE_1H_TOT_KS' : 'COND_FLOW_TOT_PRE_1H_CCHOICE', label: 'Cond Flow (m³)' },
                { id: tableName.toLowerCase() === 'ks-textile' ? 'POWER_PRE_1H_TOT_KS' : 'EM_POWER_TOT_PRE_1H_CCHOICE', label: 'Power Consumption (kW)' }
            ];
            if (!hideCapacity) {
                baseHeaders.push(
                    { id: 'Feed_Capacity', label: 'Feed Capacity' },
                    { id: 'Cond_Capacity', label: 'Cond Capacity' },
                    { id: 'Power_Capacity', label: 'Power Capacity' }
                );
            }
            return baseHeaders;
        }
        if (tableName.toLowerCase() === 'baddi') {
            return [
                { id: 'DateTime', label: 'Date Time' },
                { id: 'Baddi_Power_PRE_1h_TOT', label: 'Baddi Power (1 Hour) (kW)' },
                { id: 'EM2_POWER_PRE_1H_TOT_HMI', label: 'EM2 Power (1 Hour) (kW)' }
            ];
        }
        if (tableName.toLowerCase() === 'parle') {
            return [
                { id: 'DateTime', label: 'Date Time' },
                { id: 'FEED_FLOW_PRE_1H_TOT_PARLE', label: 'Feed Flow (m³)' },
                { id: 'COND_FLOW_TOT_PRE_1HR_PARLE', label: 'Cond Flow (m³)' },
                { id: 'POWER_PRE_1H_TOT_PARLE', label: 'Power Consumption (kW)' }
            ];
        }
        return [
            { id: 'DateTime', label: 'Date Time' },
            { id: 'FEED', label: 'Feed Flow (m³)' },
            { id: 'COND', label: 'Cond Flow (m³)' },
            { id: 'POWER', label: 'Power Consumption (kW)' }
        ];
    };

    const renderTableCell = (row, header) => {
        if (header.id === 'DateTime') {
            return formatDateTime(row.DateTime || row.Datetime);
        }
        const value = tableName.toLowerCase() === 'mohali' ||
                      tableName.toLowerCase() === 'laxmi-organics' ||
                      tableName.toLowerCase() === 'ks-textile' ||
                      tableName.toLowerCase() === 'clear-choice' ||
                      tableName.toLowerCase() === 'baddi'
            ? row[header.id]
            : row[getDataKey(header.id)];
        if (typeof value === 'number') {
            return value.toFixed(2);
        }
        if (typeof value === 'string' && !isNaN(Number(value))) {
            return Number(value).toFixed(2);
        }
        return value ?? '-';
    };

    // Excel export handler
    const handleExportExcel = () => {
        if (!data || data.length === 0) return;
        // Exclude columns with "CAPACITY" in their key
        const excludeCapacity = (row) => {
            const filtered = {};
            Object.entries(row).forEach(([key, value]) => {
                if (!key.toUpperCase().includes('CAPACITY')) {
                    filtered[key] = value;
                }
            });
            return filtered;
        };
        const exportRows = data.map(excludeCapacity);
        const ws = XLSX.utils.json_to_sheet(exportRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, tableName || 'Sheet1');
        XLSX.writeFile(wb, `${tableName || 'data'}.xlsx`);
    };

    return (
        <Paper elevation={3} className="table-container">
            <Box p={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" component="h2" gutterBottom>
                        {getDisplayName(tableName)}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleExportExcel}
                        disabled={!data || data.length === 0}
                        sx={{ minWidth: 120 }}
                    >
                        Export to Excel
                    </Button>
                </Box>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '60px', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                    S.No
                                </TableCell>
                                {getTableHeaders().map((header) => (
                                    <TableCell key={header.id}>
                                        <TableSortLabel
                                            active={orderBy === header.id}
                                            direction={order}
                                            onClick={() => handleSort(header.id)}
                                        >
                                            {header.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array(rowsPerPage).fill(null).map((_, index) => (
                                    <LoadingSkeleton key={index} />
                                ))
                            ) : (
                                sortData(data)
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                                                {page * rowsPerPage + index + 1}
                                            </TableCell>
                                            {getTableHeaders().map(header => (
                                                <TableCell key={header.id}>
                                                    {renderTableCell(row, header)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={data.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </Paper>
    );
};

export default DataTable;
